/**
 * Returns true if at least one locale value is non-empty.
 * Used for localized name/text fields (DropdownTextField).
 */
export const hasRequiredName = (values) =>
  Array.isArray(values) && values.some((v) => (v.value ?? '').trim().length > 0);

/**
 * Returns true if the URL is non-empty and a valid http/https URL.
 */
export const isValidUrl = (url) => {
  if (!url || !url.trim()) return false;
  try {
    const u = new URL(url.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Returns true if the URL is empty (optional) OR a valid http/https URL.
 * Use this for optional URL fields that should still be validated when filled.
 */
export const isOptionalUrl = (url) => {
  if (!url || !url.trim()) return true;
  return isValidUrl(url);
};

/**
 * Returns true if the URL is a valid https:// URL (plain http:// is rejected).
 * Used for the links of a 3D ad's portal, which are https-only.
 */
export const isHttpsUrl = (url) => {
  if (!url || !url.trim()) return false;
  try {
    return new URL(url.trim()).protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Returns true if start and end are both set and end is not before start.
 */
export const isDateRangeValid = (start, end) =>
  Boolean(start && end && new Date(end) >= new Date(start));

/**
 * Returns true if the value is a non-empty string or a truthy non-string.
 */
export const isRequired = (value) =>
  typeof value === 'string' ? value.trim().length > 0 : Boolean(value);

/**
 * Returns true if the value parses to a number greater than zero.
 */
export const isPositiveNumber = (value) => Number(value) > 0;

// ─── Async reachability checkers ────────────────────────────────────────────
// Each returns a Promise<boolean>. All include an 8-second timeout so the UI
// never hangs waiting on a slow or unreachable server.

/**
 * Checks an HLS stream URL (.m3u8) by sending a HEAD request.
 * Works because HLS CDNs must expose CORS headers for web players to function.
 */
export const checkHlsUrl = (url) => {
  if (!isValidUrl(url)) return false;
  // Use URL.pathname so query params (e.g. ?cookieCheck=1 from Cloudflare Access) don't break the check
  const pathname = new URL(url.trim()).pathname.toLowerCase();
  return pathname.endsWith('.m3u8');
};

/**
 * Distinguishes why a media element failed to load a URL:
 *   'unreachable'     — the server itself could not be reached (network/DNS/timeout)
 *   'invalid-format'  — the server responded, but the bytes aren't a decodable
 *                        image/video/audio (e.g. a login/error page, wrong file type)
 * Reuses checkUrl's no-cors reachability probe, which is unaffected by CORS.
 */
const classifyMediaFailure = async (url) => (
  (await checkUrl(url)) ? 'invalid-format' : 'unreachable'
);

/**
 * Checks an image URL by loading it into an Image element.
 * No CORS restriction — browsers always load images cross-origin.
 */
export const checkImageUrl = (url) => {
  if (!isValidUrl(url)) return Promise.resolve({ ok: false, reason: 'invalid-format' });
  const trimmed = url.trim();
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, reason: 'unreachable' }), 8000);
    const img = new Image();
    img.onload  = () => { clearTimeout(timer); resolve({ ok: true, reason: null }); };
    img.onerror = async () => {
      clearTimeout(timer);
      resolve({ ok: false, reason: await classifyMediaFailure(trimmed) });
    };
    img.src = trimmed;
  });
};

/**
 * Checks a video URL by loading it into a video element.
 * Only fetches metadata so it does not download the full file.
 */
export const checkVideoUrl = (url) => {
  if (!isValidUrl(url)) return Promise.resolve({ ok: false, reason: 'invalid-format' });
  const trimmed = url.trim();
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, reason: 'unreachable' }), 8000);
    const video = document.createElement('video');
    video.oncanplay = () => { clearTimeout(timer); resolve({ ok: true, reason: null }); };
    video.onerror   = async () => {
      clearTimeout(timer);
      resolve({ ok: false, reason: await classifyMediaFailure(trimmed) });
    };
    video.preload = 'metadata';
    video.src = trimmed;
  });
};

/**
 * Checks an audio URL by loading it into an Audio element.
 * Only fetches metadata so it does not download the full file.
 */
export const checkAudioUrl = (url) => {
  if (!isValidUrl(url)) return Promise.resolve({ ok: false, reason: 'invalid-format' });
  const trimmed = url.trim();
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, reason: 'unreachable' }), 8000);
    const audio = new Audio();
    audio.oncanplay = () => { clearTimeout(timer); resolve({ ok: true, reason: null }); };
    audio.onerror   = async () => {
      clearTimeout(timer);
      resolve({ ok: false, reason: await classifyMediaFailure(trimmed) });
    };
    audio.preload = 'metadata';
    audio.src = trimmed;
  });
};

/**
 * Checks a general web URL using a no-cors HEAD request.
 * Works for any http/https URL — website links, portal links, event links.
 * Because no-cors hides the response body/status, we can only detect whether
 * the server is reachable (no network error) vs unreachable (throws).
 */
export const checkUrl = async (url) => {
  if (!isValidUrl(url)) return false;
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    await fetch(url.trim(), { method: 'HEAD', mode: 'no-cors', signal: controller.signal });
    clearTimeout(timeout);
    return true;
  } catch {
    return false;
  }
};

/**
 * Checks a general web URL is reachable AND publicly accessible (not gated behind
 * a login/permission wall) — e.g. a Google Drive link shared as "Anyone with the
 * link" vs. one still set to private.
 *
 * Tries a normal (CORS) HEAD request first so the real status code can be read —
 * that's the only way to tell a public 200 apart from a private/permission-gated
 * 401/403, or a dead 404. Returns false for any non-2xx status.
 *
 * Not every host sends CORS headers on a cross-origin HEAD request, though. When
 * that happens the browser blocks us from reading the response at all (a security
 * restriction, unrelated to whether the resource itself is public) and the fetch
 * throws. In that case we fall back to a no-cors HEAD, which only proves "some
 * server answered" — it can't distinguish public from private for that host. This
 * is a hard browser limitation: a fully reliable public/private check for every
 * host would need a backend proxy, since server-to-server requests aren't subject
 * to CORS the way browser requests are.
 */
export const checkPublicUrl = async (url) => {
  if (!isValidUrl(url)) return false;
  const trimmed = url.trim();

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    const res = await fetch(trimmed, { method: 'HEAD', signal: controller.signal });
    clearTimeout(timeout);
    return res.ok;
  } catch {
    // Could be a real network failure (DNS, connection refused) or a CORS block —
    // the browser gives us no way to tell them apart. Fall back to a best-effort
    // reachability probe so at least a fully dead host is still caught.
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      await fetch(trimmed, { method: 'HEAD', mode: 'no-cors', signal: controller.signal });
      clearTimeout(timeout);
      return true;
    } catch {
      return false;
    }
  }
};

/**
 * Turns a useUrlValidation() { status, reason } result for an image/video/audio
 * field into a specific, user-facing message — instead of one generic
 * "URL is not reachable or has an invalid format" for every kind of failure.
 *
 * @param {string} url   - The raw field value (used to tell "malformed" apart
 *                         from "well-formed but unreachable").
 * @param {string} status - 'idle'|'checking'|'valid'|'invalid' from useUrlValidation.
 * @param {string|null} reason - 'unreachable'|'invalid-format'|null from useUrlValidation.
 * @param {string} kind  - 'image' | 'video' | 'audio' — used to word the message.
 */
export const describeMediaUrlStatus = (url, status, reason, kind) => {
  if (status === 'checking') return 'Checking…';
  if (status === 'valid') return 'Valid';
  if (status !== 'invalid') return '';
  if (!isValidUrl(url)) return 'URL must start with http:// or https://';
  if (reason === 'unreachable') return 'URL could not be reached';
  return `The URL is reachable but is not a valid ${kind}`;
};
